/**
 * Pinia-обёртка session keeper'а (SESSRENEW) — единственный store-источник состояния
 * сессии для значка в UI. Ядро логики — `session-keeper.ts` (чистое, без DOM);
 * здесь только реактивное зеркало и дефолтные deps браузера.
 */
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { getToken, getTokenMeta, renew, setToken, type SessionClosedInfo } from './auth'
import { useNatsStore } from './nats-store'
import { createDefaultWithLock, createSessionKeeper } from './session-keeper'
import type { LockRequester, SessionKeeper, SessionKeeperDeps, SessionPhase, SessionState } from './session-keeper'

export interface SessionStartOptions {
  /** Период тика, ms. Дефолт 30 000. */
  tickMs?: number
  /** Фолбэк порога продления, сек (если сервер не прислал). Дефолт 3600. */
  renewAfterSec?: number
  /** Подмена отдельных deps (тесты, особое окружение). */
  deps?: Partial<SessionKeeperDeps>
}

const DEFAULT_TICK_MS = 30_000
const DEFAULT_RENEW_AFTER_SEC = 3600

const readLocks = (): LockRequester | undefined =>
  typeof navigator !== 'undefined' && 'locks' in navigator ? navigator.locks : undefined

const addWindowListener: SessionKeeperDeps['addWindowListener'] = (type, fn) => {
  if (type === 'visibilitychange') {
    const handler = () => {
      if (document.visibilityState === 'visible') fn()
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }
  window.addEventListener(type, fn)
  return () => window.removeEventListener(type, fn)
}

export const useSessionStore = defineStore('session', () => {
  const phase = ref<SessionPhase>('ok')
  const expiresAt = ref<number | null>(null)
  const lastRenewAt = ref<number | null>(null)
  const lastError = ref<string | null>(null)
  const nextRenewAt = ref<number | null>(null)
  /**
   * Почему сервер закрыл сессию (O13) — текст модалки перелогина. Ставится терминальным отказом
   * продления, снимается, когда модалка закрылась (перелогин или выход).
   */
  const closedInfo = ref<SessionClosedInfo | null>(null)

  let keeper: SessionKeeper | null = null
  let unsubscribe: (() => void) | null = null

  // Модалку поднял отказ шины (kick после отзыва), а не продление: причины у вкладки нет. Один
  // вопрос серверу — продление; его 401 несёт объяснение (`onTerminal` положит его в closedInfo).
  // Keeper сам не даёт попыткам наложиться; терминальный уже keeper не спрашивает.
  const natsForClosed = useNatsStore()
  watch(
    () => natsForClosed.authExpired,
    (expired) => {
      if (!expired) {
        closedInfo.value = null
        return
      }
      if (!keeper || closedInfo.value !== null || phase.value === 'expired') return
      keeper.renewNow().catch((err: unknown) => {
        console.warn('session: не удалось узнать у сервера причину закрытия сессии', err)
      })
    },
  )

  const mirror = (state: Readonly<SessionState>) => {
    phase.value = state.phase
    expiresAt.value = state.expiresAt
    lastRenewAt.value = state.lastRenewAt
    lastError.value = state.lastError
    nextRenewAt.value = state.nextRenewAt
  }

  const defaultDeps = (): SessionKeeperDeps => {
    const natsStore = useNatsStore()
    return {
      now: () => Date.now(),
      readToken: getToken,
      readTokenMeta: getTokenMeta,
      writeToken: (token, renewAfterSec) => setToken(token, { renewAfterSec }),
      // Пустой base URL — same-origin (apps/ui за nginx / Vite-proxy).
      renew: (token, signal) => renew('', token, signal),
      withLock: createDefaultWithLock(readLocks()),
      onTerminal: (err) => {
        closedInfo.value = err.closed ?? null
        natsStore.authExpired = true
      },
      setInterval: (fn, ms) => window.setInterval(fn, ms),
      clearInterval: (handle) => window.clearInterval(handle as number),
      setTimeout: (fn, ms) => window.setTimeout(fn, ms),
      clearTimeout: (handle) => window.clearTimeout(handle as number),
      addWindowListener,
    }
  }

  /** Запустить keeper. Повторный вызов при запущенном — no-op. */
  const start = (opts: SessionStartOptions = {}) => {
    if (keeper) return
    const created = createSessionKeeper(
      { ...defaultDeps(), ...opts.deps },
      {
        tickMs: opts.tickMs ?? DEFAULT_TICK_MS,
        renewAfterSec: opts.renewAfterSec ?? DEFAULT_RENEW_AFTER_SEC,
      },
    )
    keeper = created
    unsubscribe = created.onChange(mirror)
    created.start()
    mirror(created.state)
  }

  const stop = () => {
    if (!keeper) return
    keeper.stop()
    unsubscribe?.()
    unsubscribe = null
    keeper = null
  }

  /**
   * Токен вкладки сменился (перелогин, принят от соседней вкладки): сверить состояние сразу.
   * Без запущенного keeper'а — no-op: состояния, которое могло бы устареть, нет.
   */
  const sync = () => {
    keeper?.sync()
  }

  /** «Продлить сейчас». Без запущенного keeper'а — ошибка вызывающего (не молчим). */
  const renewNow = async (): Promise<void> => {
    if (!keeper) throw new Error('session keeper не запущен: renewNow() до start()')
    await keeper.renewNow()
  }

  return { phase, expiresAt, lastRenewAt, lastError, nextRenewAt, closedInfo, start, stop, sync, renewNow }
})
